import React, { useState, useEffect, useMemo, useRef } from "react";
import { formatAccountName, formatDateForDisplay, formatDateForInput } from "../utils/formatters";
import { formatNumericInput } from "../utils/validationHelpers";
import {
  getHtmlDateMinMax,
  normalizeToYmd,
  validateScheduleFutureYmd,
} from "../utils/dateFieldConstraints";
import AddBankAccountModal from "./AddBankAccountModal";
import SelectBankAccountModal from "./SelectBankAccountModal";
import {
  WITHDRAWAL_METHOD_OPTIONS,
  FREQUENCY_OPTIONS,
  DESTINATION_TYPE_OPTIONS,
} from "../constants/accountConstants";

const WITHDRAWAL_TYPE_OPTIONS = ["Fixed Amount", "RMD", "Interest", "Income Only"];

function AddWithdrawalModal({ isOpen, onClose, household, account, onSuccess, clientToken = null, onHouseholdUpdate = null }) {
  const [formData, setFormData] = useState({
    amount: "",
    federal_tax_withholding: "",
    state_tax_withholding: "",
    frequency: "",
    start_date: "",
    method: "ACH",
    type: "",
    destination_type: "",
    destination_identifier: ""
  });
  const [error, setError] = useState("");
  const [showAddBankAccountModal, setShowAddBankAccountModal] = useState(false);
  const [showSelectBankAccountModal, setShowSelectBankAccountModal] = useState(false);
  const [newlyAddedBankAccount, setNewlyAddedBankAccount] = useState(null);
  const dateInputRef = useRef(null);

  const handleChange = (field, value) => {
    let sanitizedValue = value;
    
    if (
      ["amount", "federal_tax_withholding", "state_tax_withholding"].includes(
        field,
      )
    ) {
      sanitizedValue = formatNumericInput(value, true);
      if (sanitizedValue !== "" && field.includes("tax_withholding")) {
        const numValue = parseFloat(sanitizedValue);
        if (!isNaN(numValue)) {
          sanitizedValue = Math.min(100, numValue).toString();
        }
      }
    }

    setFormData(prev => {
      const next = { ...prev, [field]: sanitizedValue };
      if (field === "destination_type") {
        next.destination_identifier = "";
      }
      return next;
    });
    setError("");
  };

  const getInitialFormState = () => ({
    amount: "",
    federal_tax_withholding: "",
    state_tax_withholding: "",
    frequency: "",
    start_date: "",
    method: "ACH",
    type: "",
    destination_type: "",
    destination_identifier: ""
  });

  // Destination Identifier options depend on Destination Type
  const destinationIdentifierOptions = useMemo(() => {
    if (!household) return [];
    if (formData.destination_type === "Brokerage Account") {
      return (household.accounts || [])
        .filter(acc => acc.id !== account?.id)
        .map((acc, idx) => {
          const actualIndex = household.accounts.findIndex(a => a.id === acc.id);
          return {
            value: acc.id,
            label: formatAccountName(acc, actualIndex + 1, household.members || [])
          };
        });
    }
    if (formData.destination_type === "Bank Account") {
      const bankAccountOptions = (household.bank_accounts || []).map(ba => ({
        value: ba.id,
        label: `${ba.bank_name || "Bank"} - ${ba.account_number || "N/A"}`
      }));
      // Include newly added bank account if it's not already in the list
      if (newlyAddedBankAccount && !bankAccountOptions.find(opt => opt.value === newlyAddedBankAccount.id)) {
        bankAccountOptions.push({
          value: newlyAddedBankAccount.id,
          label: newlyAddedBankAccount.name
        });
      }
      return bankAccountOptions;
    }
    return [];
  }, [household, formData.destination_type, account, newlyAddedBankAccount]);

  const validateForm = () => {
    if (formData.amount === "" || formData.amount === null || formData.amount === undefined) {
      setError("Amount is required");
      return false;
    }
    const amountNum = parseFloat(formData.amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("Amount must be a positive number");
      return false;
    }
    const fed = formData.federal_tax_withholding;
    if (fed !== "" && fed !== null && fed !== undefined) {
      const n = parseFloat(fed);
      if (isNaN(n) || n < 0 || n > 100) {
        setError("Federal Tax Withholding must be between 0 and 100");
        return false;
      }
    }
    const state = formData.state_tax_withholding;
    if (state !== "" && state !== null && state !== undefined) {
      const n = parseFloat(state);
      if (isNaN(n) || n < 0 || n > 100) {
        setError("State Tax Withholding must be between 0 and 100");
        return false;
      }
    }
    if (!formData.frequency) {
      setError("Frequency is required");
      return false;
    }
    if (!formData.start_date || !formData.start_date.trim()) {
      setError("Start date is required");
      return false;
    }
    const futMsg = validateScheduleFutureYmd(
      normalizeToYmd(formData.start_date),
      "Start date",
    );
    if (futMsg) {
      setError(futMsg);
      return false;
    }
    if (!formData.method) {
      setError("Method is required");
      return false;
    }
    if (!formData.destination_type) {
      setError("Destination type is required");
      return false;
    }
    if (!formData.destination_identifier) {
      setError("Destination identifier is required");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm() || !account?.id) return;

    const startDateStr = formData.start_date?.trim();
    const withdrawalPayload = {
      amount: parseFloat(formData.amount),
      federal_tax_withholding: formData.federal_tax_withholding !== "" && formData.federal_tax_withholding !== null && formData.federal_tax_withholding !== undefined
        ? parseFloat(formData.federal_tax_withholding)
        : null,
      state_tax_withholding: formData.state_tax_withholding !== "" && formData.state_tax_withholding !== null && formData.state_tax_withholding !== undefined
        ? parseFloat(formData.state_tax_withholding)
        : null,
      frequency: formData.frequency,
      start_date: startDateStr ? formatDateForInput(startDateStr) : null,
      method: formData.method,
      type: formData.type || null,
      destination_type: formData.destination_type,
      destination_identifier: formData.destination_identifier
    };

    setFormData(getInitialFormState());
    onSuccess(withdrawalPayload);
  };

  const handleClose = () => {
    setFormData(getInitialFormState());
    setError("");
    setNewlyAddedBankAccount(null);
    onClose();
  };

  const handleAddBankAccountSuccess = (bankAccountId, bankAccountName) => {
    // Store the newly added bank account
    setNewlyAddedBankAccount({ id: bankAccountId, name: bankAccountName });
    // Auto-select the new bank account as the destination identifier
    setFormData(prev => ({
      ...prev,
      destination_identifier: bankAccountId
    }));
    // Close the add bank account modal
    setShowAddBankAccountModal(false);
    // Note: We don't trigger a refresh here because:
    // 1. The newly added bank account is stored in newlyAddedBankAccount state
    // 2. It will be displayed in the dropdown options
    // 3. A refresh would cause the AddWithdrawalModal to close unexpectedly
    // The household data will be refreshed when the user saves the withdrawal
  };

  const handleSelectExistingBankAccount = (bankAccountId) => {
    setFormData(prev => ({
      ...prev,
      destination_identifier: bankAccountId
    }));
    setShowSelectBankAccountModal(false);
  };

  const handleAddNewBankAccount = () => {
    setShowSelectBankAccountModal(false);
    setShowAddBankAccountModal(true);
  };

  // Get the label for the selected bank account
  const getSelectedBankAccountLabel = () => {
    if (!formData.destination_identifier) return null;
    
    // Check if it's the newly added bank account
    if (newlyAddedBankAccount && newlyAddedBankAccount.id === formData.destination_identifier) {
      return newlyAddedBankAccount.name;
    }
    
    // Find in household bank accounts
    const bankAccount = (household?.bank_accounts || []).find(ba => ba.id === formData.destination_identifier);
    if (bankAccount) {
      return `${bankAccount.bank_name || "Bank"} - ${bankAccount.account_number || "N/A"}`;
    }
    
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormState());
      setError("");
      setNewlyAddedBankAccount(null);
      setShowSelectBankAccountModal(false);
      setShowAddBankAccountModal(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const scheduleBounds = getHtmlDateMinMax("schedule_future");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Add Withdrawal</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={formData.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                onWheel={(e) => e.target.blur()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Federal Tax Withholding (%) <span className="text-gray-500 text-xs">0–100</span>
              </label>
              <input
                type="number"
                min={0}
                max={100}
                step="any"
                value={formData.federal_tax_withholding}
                onChange={(e) => handleChange("federal_tax_withholding", e.target.value)}
                onWheel={(e) => e.target.blur()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State Tax Withholding (%) <span className="text-gray-500 text-xs">0–100</span>
              </label>
              <input
                type="number"
                min={0}
                max={100}
                step="any"
                value={formData.state_tax_withholding}
                onChange={(e) => handleChange("state_tax_withholding", e.target.value)}
                onWheel={(e) => e.target.blur()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => handleChange("frequency", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select frequency</option>
                {FREQUENCY_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <div
                className="relative cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => { if (dateInputRef.current?.showPicker) dateInputRef.current.showPicker(); else dateInputRef.current?.focus(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); dateInputRef.current?.showPicker?.(); } }}
              >
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={formData.start_date ? formatDateForDisplay(formatDateForInput(formData.start_date)) : ""}
                    placeholder="mm-dd-yyyy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white cursor-pointer pointer-events-none pr-10"
                    aria-readonly
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <input
                  ref={dateInputRef}
                  type="date"
                  min={scheduleBounds.min}
                  max={scheduleBounds.max}
                  value={formData.start_date ? formatDateForInput(formData.start_date) : ""}
                  onChange={(e) => handleChange("start_date", e.target.value ? formatDateForInput(e.target.value) : "")}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onWheel={(e) => e.target.blur()}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer pointer-events-none"
                  style={{ cursor: "pointer" }}
                  tabIndex={-1}
                  aria-hidden
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Method <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.method}
                onChange={(e) => handleChange("method", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select method</option>
                {WITHDRAWAL_METHOD_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.type || ""}
                onChange={(e) => handleChange("type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select type</option>
                {WITHDRAWAL_TYPE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.destination_type}
                onChange={(e) => handleChange("destination_type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select destination type</option>
                {DESTINATION_TYPE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination Identifier <span className="text-red-500">*</span>
              </label>
              {formData.destination_type === "Bank Account" ? (
                // Modal-based selection for Bank Account
                <div>
                  {getSelectedBankAccountLabel() ? (
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md bg-gray-50 text-gray-900 text-sm">
                        {getSelectedBankAccountLabel()}
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowSelectBankAccountModal(true)}
                        className="px-2 py-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium border border-emerald-300 rounded-md hover:bg-emerald-50 transition-colors"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowSelectBankAccountModal(true)}
                      className="w-full px-2 py-1.5 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-emerald-500 hover:text-emerald-600 transition-colors flex items-center justify-center gap-1.5 text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Select Bank Account
                    </button>
                  )}
                </div>
              ) : (
                // Dropdown for Brokerage Account or when no type selected
                <select
                  value={formData.destination_identifier}
                  onChange={(e) => handleChange("destination_identifier", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={!formData.destination_type}
                >
                  <option value="">
                    {formData.destination_type
                      ? "Select destination"
                      : "Select destination type first"}
                  </option>
                  {destinationIdentifierOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            Add Withdrawal
          </button>
        </div>
      </div>

      {/* Select Bank Account Modal */}
      <SelectBankAccountModal
        isOpen={showSelectBankAccountModal}
        onClose={() => setShowSelectBankAccountModal(false)}
        onSelectExisting={handleSelectExistingBankAccount}
        onAddNew={handleAddNewBankAccount}
        bankAccounts={household?.bank_accounts || []}
      />

      {/* Add Bank Account Modal */}
      <AddBankAccountModal
        isOpen={showAddBankAccountModal}
        onClose={() => {
          setShowAddBankAccountModal(false);
          // Reopen select modal if user cancels adding new bank account
          setShowSelectBankAccountModal(true);
        }}
        household={household}
        onSuccess={handleAddBankAccountSuccess}
        clientToken={clientToken}
      />
    </div>
  );
}

export default AddWithdrawalModal;
